<!-- app/javascript/dashboard/routes/dashboard/clusters/AddMemberPanel.vue -->
<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  clusterId: { type: [String, Number], required: true },
  roles: { type: Array, default: () => [] },
});

const store = useStore();
const { t } = useI18n();

const memberType = ref('Contact');
const memberId = ref(null);
const role = ref('');
const isPrimary = ref(false);

const add = async () => {
  if (!memberId.value) return;
  await store.dispatch('clusterMemberships/create', {
    clusterId: props.clusterId,
    membership: {
      member_type: memberType.value,
      member_id: memberId.value,
      role: role.value || null,
      is_primary: isPrimary.value,
    },
  });
  memberId.value = null;
  role.value = '';
  isPrimary.value = false;
};
</script>

<template>
  <div class="flex flex-col gap-2 p-3 border border-n-weak rounded-lg bg-n-solid-2">
    <p class="text-xs font-semibold tracking-wide uppercase text-n-slate-10">
      {{ t('CLUSTERS.MEMBERS.ADD') }}
    </p>

    <!-- Member type -->
    <div class="flex gap-4">
      <label class="flex items-center gap-1.5 text-sm text-n-slate-12 cursor-pointer">
        <input v-model="memberType" type="radio" value="Contact" class="accent-n-brand" />
        {{ t('CLUSTERS.MEMBERS.CONTACT') }}
      </label>
      <label class="flex items-center gap-1.5 text-sm text-n-slate-12 cursor-pointer">
        <input v-model="memberType" type="radio" value="Ticket" class="accent-n-brand" />
        {{ t('CLUSTERS.MEMBERS.TICKET') }}
      </label>
    </div>

    <!--
      Member ID: numeric input as a deliberate v1 minimum.
      TODO: swap for the dashboard's existing contact/ticket search component
      (find in EmpresaDetails.vue search section) so operators pick by name, not id.
    -->
    <input
      v-model.number="memberId"
      type="number"
      :placeholder="t('CLUSTERS.MEMBERS.MEMBER_ID')"
      class="h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
    />

    <!-- Role -->
    <select
      v-model="role"
      class="h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
    >
      <option value="">{{ t('CLUSTERS.MEMBERS.NO_ROLE') }}</option>
      <option v-for="r in roles" :key="r" :value="r">{{ r }}</option>
    </select>

    <!-- Is primary -->
    <label class="flex items-center gap-1.5 text-sm text-n-slate-12 cursor-pointer">
      <input v-model="isPrimary" type="checkbox" class="accent-n-brand" />
      {{ t('CLUSTERS.MEMBERS.IS_HEAD') }}
    </label>

    <button
      type="button"
      :disabled="!memberId"
      class="self-start text-sm bg-n-brand text-white rounded px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
      @click="add"
    >
      {{ t('CLUSTERS.MEMBERS.ADD_BUTTON') }}
    </button>
  </div>
</template>
